import React, {useEffect, useState} from 'react';
import {Box, Stack} from '@mui/material';
import {Outlet, useNavigate} from 'react-router-dom';
import {useObservable} from '@ngneat/react-rxjs';
import {JobInvoice, JobInvoiceStatus, jobsInvoiceQuery, jobsInvoiceService} from '@store/invoices/jobsInvoice';
import {useEffectFn} from '@ngneat/effects-hooks';
import {searchJobsInvoiceEffect} from '@store/invoices/jobsInvoice/jobsInvoice.effects';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import NewTableComponent from '@components/table/NewTable.component';
import I18n from '@utils/i18n.utils';
import {useTranslation} from 'react-i18next';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import {ConfirmActionProps, OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import {finalize, Observable} from 'rxjs';
import ConfirmModal from '@components/modal/Confirm.modal';
import {useSnackbar} from 'notistack';
import SelectComponent from '@components/inputs/select/Select.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import {Sizes} from '@constants/sizes.constants';

const InvoicesValidationTab = () => {
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const searchJobsInvoice = useEffectFn(searchJobsInvoiceEffect);

  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);

  const [{jobsInvoice, loading}] = useObservable(jobsInvoiceQuery.jobsInvoice$);

  const [sortModel] = useObservable(jobsInvoiceQuery.sort$);
  const [tablePagination] = useObservable(jobsInvoiceQuery.pagination$);

  const [status] = useObservable(jobsInvoiceQuery.status$);
  const [from] = useObservable(jobsInvoiceQuery.from$);


  useEffect(() => {
    searchJobsInvoice({tablePagination, sort: sortModel, status: status, from});
  }, [searchJobsInvoice, sortModel, tablePagination, status, from]);

  const handleConfirmValidation = (type: OtherActionType, jobInvoice: JobInvoice) => (handleStopLoading: () => void) => {
    let validationObservable: Observable<any> | undefined = undefined;
    switch (type) {
      case OtherActionType.DELETE:
        validationObservable = jobsInvoiceService.deleteEmptyJobInvoice(jobInvoice.id);
        break;
    }
    validationObservable?.pipe(finalize(handleStopLoading)).subscribe({
      next: () => {
        enqueueSnackbar(t(`${OtherActionType.translationKey(type)}.success.jobInvoice`), {variant: 'success'});
        setOpenConfirmAction(false);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <Stack direction="column" spacing={2} flex={1} mt={2}>
      <Stack direction="row" spacing={2}>
        <Box>
          <DatePickerComponent
            size="small"
            inputWidth={Sizes.datePickerWidth}
            placeholder={t('invoices.invoicesValidation.filters.date')}
            value={from}
            handleChange={jobsInvoiceService.setFilters('from')}/>
        </Box>
        <Box>
          <SelectComponent
            size="small"
            inputWidth={140}
            value={status}
            items={JobInvoiceStatus.selectItems()}
            handleChange={jobsInvoiceService.setStatus}
            placeholder={t('invoices.invoicesValidation.filters.status')}/>
        </Box>
      </Stack>
      <Stack flex={1} spacing={2} pb="25px" mt="20px">
        <NewTableComponent
          loading={loading}
          columns={[
            COLUMN_TYPES.TEXT_BOLD({
              field: 'createdAt',
              label: t('invoices.invoicesValidation.table.generated'),
              valueFormatter: (params) => params.value ? I18n.t('global.date', {date: new Date(params.value)}) : '',
              width: 110,
            }),
            COLUMN_TYPES.TEXT({
              field: 'clientOrganisations',
              color: 'secondary',
              label: t('invoices.invoicesValidation.table.clients'),
              fullWidth: true,
              minWidth: 250,
              valueFormatter: (params) => params.value.length > 1 ? `${I18n.t('global.multiple')} (${params.value.length})` : params.value?.[0]?.name,
            }),
            COLUMN_TYPES.TEXT({
              field: 'typologies',
              color: 'secondary',
              label: t('invoices.invoicesValidation.table.types'),
              fullWidth: true,
              minWidth: 250,
            }),
            COLUMN_TYPES.TEXT({
              field: 'period',
              color: 'secondary',
              label: t('invoices.invoicesValidation.table.period'),
              valueGetter: (params: GridValueGetterParams<any, JobInvoice>) => ((!params.row.from || !params.row.to) ? '-' : t('global.period', {
                from: new Date(params.row.from),
                to: new Date(params.row.to)
              })),
              fullWidth: true,
              minWidth: 230,
            }),
            COLUMN_TYPES.TEXT({
              field: 'user',
              color: 'secondary',
              label: t('invoices.invoicesValidation.table.user'),
              valueGetter: (params: GridValueGetterParams<any, JobInvoice>) => params.row.author ? params.row.author.email : '',
              fullWidth: true,
              minWidth: 200,
            }),
            COLUMN_TYPES.TEXT({
              field: 'clientInvoicesGeneratedCount',
              label: t('invoices.invoicesValidation.table.clientInvoicesGeneratedCount'),
              color: 'secondary',
              headerName: I18n.t('invoices.invoicesValidation.table.invoicesGeneratedCount'),
              width: 150,
            }),
            COLUMN_TYPES.TEXT({
              field: 'clientInvoicesKeptCount',
              label: t('invoices.invoicesValidation.table.clientInvoicesKeptCount'),
              color: 'primary',
              headerName: I18n.t('invoices.invoicesValidation.table.invoicesKeptCount'),
              width: 150,
              valueGetter: (params: GridValueGetterParams<any, JobInvoice>) => params.row.status === JobInvoiceStatus.TO_BE_VALIDATED ? '-' : params.value,
            }),
            COLUMN_TYPES.SELECT({
              field: 'invoice.status',
              label: t('global.status'),
              options: JobInvoiceStatus.selectItems().concat({
                label: t('global.jobInvoiceStatus.EMPTY'),
                value: JobInvoiceStatus.EMPTY,
                backgroundColor: 'white',
              }),
              sortable: true,
              disabled: () => true,
              valueGetter: (params: GridValueGetterParams<any, JobInvoice>) => params.row.status,
              width: 150,
            }),
            COLUMN_TYPES.ACTIONS({
              handleShowRow: (jobInvoice: JobInvoice) => navigate(`${jobInvoice.id}`),
              otherActions: [
                {
                  type: OtherActionType.DELETE,
                  hidden: (jobInvoice: JobInvoice) => jobInvoice.status !== JobInvoiceStatus.EMPTY,
                  handleClick: (jobInvoice: JobInvoice) => setOpenConfirmAction({
                    type: OtherActionType.DELETE,
                    row: jobInvoice
                  }),
                },
                {
                  type: OtherActionType.PDF,
                  handleClick: (jobInvoice: JobInvoice) => {
                    jobsInvoiceService.printJobInvoice(jobInvoice.id).subscribe({
                      error: (err) => enqueueSnackbar(err.text, err.options),
                    });
                  },
                  hidden: (jobInvoice: JobInvoice) => jobInvoice.status === JobInvoiceStatus.EMPTY
                },
                {
                  type: OtherActionType.ZIP,
                  handleClick: (jobInvoice: JobInvoice) => {
                    jobsInvoiceService.zipJobInvoice(jobInvoice.id).subscribe({
                      error: (err) => enqueueSnackbar(err.text, err.options),
                    });
                  },
                  hidden: (jobInvoice: JobInvoice) => jobInvoice.status === JobInvoiceStatus.EMPTY
                },
                {
                  type: OtherActionType.CSV,
                  handleClick: (jobInvoice: JobInvoice) => {
                    jobsInvoiceService.csvJobInvoice(jobInvoice.id).subscribe({
                      error: (err) => enqueueSnackbar(err.text, err.options),
                    });
                  },
                  hidden: (jobInvoice: JobInvoice) => jobInvoice.status === JobInvoiceStatus.EMPTY
                },
              ],
            }),
          ]}
          handlePageSizeChange={jobsInvoiceService.setPagination('limit')}
          pageSize={tablePagination.limit}
          handlePageChange={(page) => jobsInvoiceService.setPagination('page')(page + 1)}
          page={tablePagination.page - 1}
          handleSortModelChange={jobsInvoiceService.setSort}
          sortModel={[sortModel]}
          disableRowSelection
          totalRows={jobsInvoiceQuery.totalRows}
          data={jobsInvoice}/>
        {!!openConfirmAction && (
          <ConfirmModal
            handleClose={() => setOpenConfirmAction(false)}
            description={t(`${OtherActionType.translationKey(openConfirmAction.type)}.description.jobInvoice`)}
            handleValidate={handleConfirmValidation(openConfirmAction.type, openConfirmAction.row)}
            title={t(`${OtherActionType.translationKey(openConfirmAction.type)}.title`)}
            validateButton={OtherActionType.label(openConfirmAction.type)}/>
        )}
        <Outlet/>
      </Stack>
    </Stack>

  );
};

export default InvoicesValidationTab;
